// src/pages/MARegisterPage.js
// Simplified registration for employee role (via invitation link)

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { authApi } from '../axiosInstance';

export default function MARegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token] = useState(() => searchParams.get('token') || '');
  const [emailFromUrl] = useState(() => searchParams.get('email') || '');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  // CSRF priming
  useEffect(() => {
    authApi.get('/csrf-token', { withCredentials: true }).catch(() => {});
  }, []);

  // Guard: token required
  useEffect(() => {
    if (!token) {
      setErrorMessage('Ungültiger oder fehlender Einladungslink.');
    }
  }, [token]);

  // Clear existing session if present (same pattern as RegisterPage)
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch('/auth/whoami', { credentials: 'include' });
        const who = r.ok ? await r.json() : null;
        if (who?.user) {
          const ret = encodeURIComponent(window.location.href);
          window.location.href = `/auth/logout?return=${ret}`;
        }
      } catch { /* ignore */ }
    })();
  }, [token]);

  const passwordValid = password.length >= 12;
  const passwordsMatch = password === passwordConfirm;
  const canSubmit = token && firstName.trim() && lastName.trim() && passwordValid && passwordsMatch && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorMessage('');
    setSubmitting(true);

    try {
      let csrfToken = '';
      try {
        const { data } = await authApi.get('/csrf-token', { withCredentials: true });
        csrfToken = data?.csrfToken || '';
      } catch { /* */ }

      await authApi.post('/register', {
        token,
        email: emailFromUrl,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
      }, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.error
        || (typeof err?.response?.data === 'string' ? err.response.data : null)
        || 'Registrierung fehlgeschlagen. Bitte erneut versuchen.';
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
        <div className="mb-10">
          <Logo variant="wordmark" size="xxxl" />
        </div>
        <div className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md text-center">
          <div className="text-green-400 text-4xl mb-4">✓</div>
          <h2 className="text-xl font-bold mb-2">Registrierung erfolgreich</h2>
          <p className="text-gray-400 text-sm mb-6">
            Sie können sich jetzt im Mitarbeiter-Portal anmelden.
          </p>
          <button
            onClick={() => navigate('/ma-login')}
            className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-2xl transition"
          >
            Zum Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
      <div className="mb-10">
        <Logo variant="wordmark" size="xxxl" />
      </div>

      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold mb-2">Konto erstellen</h2>
        <p className="text-gray-400 text-sm">
          Nach der Registrierung können Sie sich im Mitarbeiter-Portal anmelden.
        </p>
      </div>

      {errorMessage && (
        <div className="bg-red-600 text-white px-4 py-2 rounded-2xl mb-6 w-full max-w-md text-center">
          {errorMessage}
        </div>
      )}

      {emailFromUrl && (
        <div className="text-gray-400 text-sm mb-4 w-full max-w-md text-center">
          Registrierung für <span className="text-white font-medium">{emailFromUrl}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md"
      >
        <div className="mb-4">
          <label className="block mb-2 font-semibold text-sm">Vorname</label>
          <input
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
            className="w-full p-3 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-amber-500"
            placeholder="Max"
            autoComplete="given-name"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-semibold text-sm">Nachname</label>
          <input
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            required
            className="w-full p-3 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-amber-500"
            placeholder="Mustermann"
            autoComplete="family-name"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-semibold text-sm">Passwort</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={8}
            className="w-full p-3 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-amber-500"
            placeholder="Mindestens 12 Zeichen"
            autoComplete="new-password"
          />
          {password && !passwordValid && (
            <p className="text-red-400 text-xs mt-1">Mindestens 12 Zeichen erforderlich</p>
          )}
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-semibold text-sm">Passwort wiederholen</label>
          <input
            type="password"
            value={passwordConfirm}
            onChange={e => setPasswordConfirm(e.target.value)}
            required
            className="w-full p-3 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-amber-500"
            placeholder="Passwort bestätigen"
            autoComplete="new-password"
          />
          {passwordConfirm && !passwordsMatch && (
            <p className="text-red-400 text-xs mt-1">Passwörter stimmen nicht überein</p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className={`w-full font-bold py-3 rounded-2xl transition ${
            canSubmit
              ? 'bg-amber-500 hover:bg-amber-400 text-black'
              : 'bg-slate-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {submitting ? 'Wird registriert…' : 'Registrieren'}
        </button>
      </form>
    </div>
  );
}
